import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { PartnerDebtDto } from '@interfaces/partner-income-tax/responses/partner-income-tax.response';
import {
  debtDisplayName,
  formatAssetName,
  formatCompactMoney,
  formatThousands,
  valueForYear,
} from '@features/dashboard/performance/pages/dfp/components/partner-income-tax/utils/partner-income-tax-format.util';

function normalizeForCompare(text: string): string {
  return text
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, ' ')
    .trim();
}

/** A descrição da dívida do IRPF costuma começar pelo nome do credor; aí ela não acrescenta nada. */
export function startsWithCreditor(description: string, creditor: string): boolean {
  const text = normalizeForCompare(description);
  const name = normalizeForCompare(creditor);
  return !text || (!!name && text.startsWith(name));
}

/**
 * "Dívidas e ônus reais" (aba interna Dívidas e ônus): tabela como a da mutação patrimonial, com credor e finalidade,
 * o saldo em 31/12 de cada ano da janela e a garantia vinculada (o bem que o backend ligou à dívida).
 */
@Component({
  selector: 'app-pit-debts-table',
  standalone: true,
  templateUrl: './debts-table.component.html',
  styleUrl: './debts-table.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DebtsTableComponent {
  readonly debts = input.required<PartnerDebtDto[]>();
  readonly years = input.required<number[]>();
  /** Total das dívidas do sócio no último ano (do indicador do backend). */
  readonly total = input<number>(0);

  protected readonly money = formatCompactMoney;

  protected readonly lastYear = computed(() => {
    const years = this.years();
    return years.length ? years[years.length - 1] : null;
  });

  /** Linhas da tabela, com a finalidade escondida quando só repete o credor. */
  protected readonly rows = computed(() => {
    const years = this.years();
    return this.debts().map(debt => {
      const creditor = formatAssetName(debt.creditorName);
      const description = formatAssetName(debt.description);
      // Com o nome curto do adendo v2, ele vira o título e o credor desce para a legenda.
      const hasShortName = !!debt.shortName?.trim();
      const title = debtDisplayName(debt);
      // Credor na legenda só quando o título ainda não o diz (N16).
      const titleHasCreditor = normalizeForCompare(title).includes(normalizeForCompare(creditor));
      return {
        debt,
        creditor: title,
        purpose: hasShortName
          ? titleHasCreditor
            ? ''
            : creditor
          : startsWithCreditor(description, creditor)
            ? ''
            : description,
        secured: debt.securedAssetName ? formatAssetName(debt.securedAssetName) : '',
        values: years.map(year => formatThousands(valueForYear(debt.values, year))),
      };
    });
  });
}
